import { useTranslation } from 'react-i18next';

import { Card } from '@codes/ui-kit/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { CardView } from '@lemoncloud/lemon-payments-api';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { CardItem } from '@web/app/features/my-page/components/CardItem';
import { UsageDetail } from '@web/app/features/styling/components/UsageDetail';
import { CardRegistrationForm, ErrorCard } from '@web/app/shared';
import { ChevronRight, Plus } from 'lucide-react';

import { useLoader } from '@codes/loader';
import { BulkCardUpdateBody, cardQueryKeys, useBulkUpdateCards, useCards, useDeleteCard } from '@codes/payments';
import { ListResult } from '@codes/shared';
import { Button, Dialog, DialogContent, DialogTitle, DialogTrigger, SharedSpinner } from '@codes/ui-kit';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

export const BillingTab = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { setIsLoading } = useLoader();
    const {
        data: cardsData,
        isLoading: isCardsFetching,
        isError,
        error,
        refetch,
    } = useCards({ stereo: 'stripe', view: 'mine' });

    const bulkUpdateCards = useBulkUpdateCards();
    const deleteCard = useDeleteCard();

    const handleDefaultCardClick = async (card: CardView) => {
        const bulkCardBody: BulkCardUpdateBody = {
            list: [
                ...(cardsData?.list || []).map(origin =>
                    origin.id === card.id
                        ? { ...card, isDefault: true }
                        : {
                              ...origin,
                              isDefault: false,
                          }
                ),
            ],
        };

        setIsLoading(true);
        try {
            await bulkUpdateCards.mutateAsync(bulkCardBody, {
                onSuccess: async (updatedCards: CardView[]) => {
                    queryClient.setQueryData(cardQueryKeys.list({ stereo: 'stripe', view: 'mine' }), (oldData: any) => {
                        if (!oldData) {
                            return oldData;
                        }

                        return {
                            ...oldData,
                            data: updatedCards,
                            list: updatedCards,
                        };
                    });
                    toast({ title: `Successfully updated.`, duration: 2000 });
                },
                onError: error => {
                    console.error('Failed to update card:', error);
                    toast({
                        description: `Failed to update! ${error.toString()}`,
                        duration: 2000,
                    });
                },
            });
        } catch (error) {
            toast({
                title: `Failed to update card: ${error.message}`,
                duration: 2000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteCard = async (card: CardView) => {
        setIsLoading(true);
        try {
            await deleteCard.mutateAsync(card.id as string, {
                onSuccess: async (deleted: CardView) => {
                    queryClient.setQueryData<ListResult<CardView>>(
                        cardQueryKeys.list({ stereo: 'stripe', view: 'mine' }),
                        oldData => {
                            if (!oldData) {
                                return oldData;
                            }
                            return {
                                ...oldData,
                                list: oldData.list.filter(c => c.id !== deleted.id),
                                total: (oldData.total || 1) - 1,
                            };
                        }
                    );
                    toast({ title: t('myPage.cardDeletionComplete'), duration: 2000 });
                },
            });
        } catch (error) {
            toast({
                title: `Failed to delete card: ${error.message}`,
                duration: 2000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-[500px] mx-auto flex flex-col gap-[26px]">
            {/* TODO: 결제 예정 */}
            <div>
                <div className="w-full text-[18px] font-medium mb-4">{t('myPage.paymentScheduled')}</div>
                <div className="flex flex-col gap-2">
                    <Card className="rounded-lg flex flex-col gap-[10px] dark:bg-[#2e2e2e]">
                        <div className="flex items-center justify-between text-sm">
                            <div className="font-medium">January's {t('myPage.paymentScheduled')}</div>
                            <Dialog>
                                <DialogTrigger asChild className="flex justify-center">
                                    <button className="text-sm text-sub-title flex items-center">
                                        {t('myPage.viewDetails')}
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="max-w-[500px] p-0" aria-describedby={undefined}>
                                    <UsageDetail />
                                </DialogContent>
                            </Dialog>
                        </div>
                        <div className="text-[18px] text-point font-medium flex items-center justify-between">
                            <div>{t('myPage.totalExpectedAmount')}</div>
                            <div>$0</div>
                        </div>
                    </Card>
                </div>
            </div>
            {/* 결제 내역 */}
            <div>
                <div className="w-full text-[18px] font-medium mb-[10px]">{t('myPage.paymentHistory')}</div>
                <div className="flex flex-col gap-2">
                    <Select>
                        <SelectTrigger>
                            <SelectValue placeholder="" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('myPage.all')}</SelectItem>
                            <SelectItem value="1">2025. 02</SelectItem>
                        </SelectContent>
                    </Select>
                    <Card className="rounded-lg flex flex-col gap-1 dark:bg-[#2e2e2e]">
                        <div className="flex items-center justify-between text-sm mb-[6px]">
                            <div className="font-medium">January's {t('myPage.paymentHistory')}</div>
                            <Dialog>
                                <DialogTrigger asChild className="flex justify-center">
                                    <button className="text-sm text-sub-title flex items-center">
                                        {t('myPage.viewDetails')}
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="max-w-[500px] p-0" aria-describedby={undefined}>
                                    <UsageDetail />
                                </DialogContent>
                            </Dialog>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <div>{t('myPage.paymentDate')}</div>
                            <div className="font-medium">2025. 03. 05</div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <div>{t('myPage.status')}</div>
                            <div className="font-medium">{t('myPage.paymentCompleted')}</div>
                        </div>
                        <div className="text-[18px] text-point font-medium flex items-center justify-between">
                            <div>{t('myPage.totalAmount')}</div>
                            <div>$0</div>
                        </div>
                    </Card>
                </div>
            </div>
            {/* TODO: 결제 수단 */}
            <div>
                <div className="w-full text-[18px] font-medium mb-4">{t('myPage.paymentMethod')}</div>
                <div className="flex flex-col gap-2">
                    {isError && <ErrorCard error={error} resetError={() => refetch()} />}
                    {isCardsFetching && (
                        <div className="w-full flex justify-center">
                            <SharedSpinner />
                        </div>
                    )}
                    {cardsData?.total === 0 && (
                        <Card className="h-[172px] rounded-lg flex flex-col items-center justify-center text-center whitespace-pre-wrap dark:bg-[#2e2e2e]">
                            <div className="text-placeholder">{t('myPage.noPaymentMethod')}</div>
                            <Dialog>
                                <DialogTrigger asChild>
                                    <button className="w-10 h-10 rounded-full bg-badge flex items-center justify-center mt-6">
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="max-w-[500px] px-6">
                                    <DialogTitle className="mb-7 text-[20px] text-text">
                                        {t('myPage.registerCard')}
                                    </DialogTitle>
                                    <Elements stripe={stripePromise}>
                                        <CardRegistrationForm />
                                    </Elements>
                                </DialogContent>
                            </Dialog>
                        </Card>
                    )}
                    {cardsData?.list.map(card => (
                        <CardItem
                            key={card.id}
                            card={card}
                            onDefaultCardClick={handleDefaultCardClick}
                            onDeleteCard={handleDeleteCard}
                        />
                    ))}
                </div>
                {(cardsData?.total || 0) > 0 && (
                    <Dialog>
                        <DialogTrigger asChild>
                            <Button
                                size="lg"
                                className="bg-badge text-sub-title w-full mt-[34px] flex gap-1 hover:bg-badge"
                            >
                                <Plus className="w-4 h-4" />
                                {t('myPage.registerCard')}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-[500px] px-6">
                            <DialogTitle className="mb-7 text-[20px] text-text">{t('myPage.registerCard')}</DialogTitle>
                            <Elements stripe={stripePromise}>
                                <CardRegistrationForm />
                            </Elements>
                        </DialogContent>
                    </Dialog>
                )}
            </div>
        </div>
    );
};
